import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import type { QuickAction } from "./types";

type QuickActionsProps = {
  actions: QuickAction[];
  title?: string;
  description?: string;
  emptyMessage?: string;
  onAction?: (action: QuickAction) => void;
};

export const QuickActions: React.FC<QuickActionsProps> = ({
  actions,
  title = "Quick Actions",
  description = "Common tasks to jump back into operations",
  emptyMessage = "No quick actions are available right now.",
  onAction,
}) => {
  return (
    <Card className="border border-gray-200/60 bg-white/95 shadow-lg dark:border-gray-700/60 dark:bg-gray-900">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</CardTitle>
        <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {actions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200/70 bg-gray-50/70 px-4 py-6 text-center text-sm text-muted-foreground dark:border-gray-700/60 dark:bg-gray-800/60">
            {emptyMessage}
          </div>
        ) : (
          actions.map((action) => (
            <Button
              key={action.id}
              variant="outline"
              size="lg"
              className="flex h-auto w-full items-start justify-start gap-3 rounded-xl border-gray-200/70 bg-white/80 px-3 py-3 text-left shadow-sm transition-colors hover:bg-white hover:text-foreground dark:border-gray-700/60 dark:bg-gray-900/80 dark:hover:bg-gray-800"
              onClick={() => {
                action.onClick();
                onAction?.(action);
              }}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                {action.icon}
              </span>
              <span className="flex-1 text-xs">
                <span className="block font-semibold text-foreground">{action.title}</span>
                <span className="block text-muted-foreground/80">{action.description}</span>
              </span>
            </Button>
          ))
        )}
      </CardContent>
    </Card>
  );
};
